import { Button, Container, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Icon, SnackBarContext } from "components";
import { theme } from "config";
import React from "react";
import { DropzoneRootProps, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { XLSX_UserRow } from 'types';
import { read, utils } from "xlsx";

export interface IUploadProps {
  onUploadComplete?(data: XLSX_UserRow[]): void,
  onError?(error: string): void,
}

export const UploadXLSX = (props: IUploadProps) => {
  const { t, i18n } = useTranslation();
  const { toast }   = React.useContext(SnackBarContext);

  const onDrop = React.useCallback((acceptedFiles: Blob[]) => {
    acceptedFiles.forEach((fileData: Blob) => {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;

      reader.onabort = (err) => {
        console.error('file reading was aborted', err);
        toast(t("failed_parse_file"), "error");
      }
      reader.onerror = (err) => {
        console.error('file reading has failed', err);
        toast(t("failed_parse_file"), "error");
      }
      reader.onload = (e) => {
        try {
          const bstr = e.target.result;
          const workbook = read(bstr, { type: rABS ? 'binary' : 'array' });
          const jsonFromExcel = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]]
          ) as XLSX_UserRow[];

          props.onUploadComplete(jsonFromExcel);
        } catch (error) {
          console.error(error);
          props.onError(error);
          toast(t("failed_parse_file"), "error");
        }
      };
      reader.addEventListener('error', () => {
        toast(t("failed_parse_file"), "error")
        props.onError(t("failed_parse_file"));
      });
      if (rABS) reader.readAsBinaryString(fileData);
      else reader.readAsArrayBuffer(fileData);
    })
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({ onDrop });

  return (
    <Container maxWidth='md'>
      <Grid
        container
        flexDirection = 'column'
        width         = '100%'
        padding       = '0'
      >
          <Grid item xs={12} width='100%'>
            <p>{t('click_here_to_download_user_import_excelsheet')} <Link href={process.env.IMPORT_USERS_EXCEL_URL} download>{t('here')}!</Link></p>
          </Grid>
        <Grid item xs={12} width='100%'>
        <UploadForm {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} />
          <LargeIcon iconId='download' />
          <Typography>{t('drag_and_drop_description')}</Typography>
          <Typography>{t('common_or')}</Typography>
          <Button variant='outlined'>{t('common_browse')}</Button>
        </UploadForm>
        {/* <UploadXLSX
          onUploadComplete = {(users) => setUsers(users)}
          onError          = {onClose}
        /> */}
        </Grid>
      </Grid>
    </Container>
  )
}

const getColor = (props: DropzoneRootProps) => {
  if (props.isDragAccept) {
    return theme.palette.success.main;
  }
  if (props.isDragReject) {
    return theme.palette.error.main;
  }
  if (props.isFocused) {
    return theme.palette.primary.main;
  }
  return grey[300];
}

const UploadForm = styled.div`
  flex:             1;
  display:          flex;
  flex-direction:   column;
  align-items:      center;
  width:            100%;
  padding:          ${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(5)};
  border-width:     2px;
  border-color:     ${(props) => getColor(props)};
  border-style:     dashed;
  background-color: transparent;
  outline:          none;
  transition:       border 0.3s ease-in-out;
`;

const LargeIcon = styled(Icon)`
  width:  8rem;
  height: 8rem;

  svg {
    width:  100%;
    height: 100%;
  }
`;

const Link = styled.a`
  text-decoration: underline;
  cursor:          pointer;
  font-weight:     500;
  color:           black;
`;